"use client";

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "./useAuthStore";
import { APP_ORIGIN } from "@/constants";
import { defaultLocale, isLocale } from "@/i18n";
import { withLocale } from "@/helpers";

/**
 * Hook to handle verification redirect logic
 *
 * 1. Redirect to verification if user hasn't completed it
 * 2. Redirect away from verification if user has already completed it → product APP_ORIGIN
 * 3. Redirect default routes (root and locale routes) to product APP_ORIGIN
 */
export const useVerificationRedirect = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuthStore();
  const [redirectAvailable, setRedirectAvailable] = useState(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    const seg = pathname.split("/").filter(Boolean)[0];
    const locale = isLocale(seg) ? seg : defaultLocale;

    const goToProduct = () => {
      const base = APP_ORIGIN || "http://localhost:3001";
      window.location.assign(`${base.replace(/\/+$/, "")}/${locale}`);
    };

    if (
      !user.onboardingCompleted &&
      user.role !== "demo" &&
      typeof window !== "undefined" &&
      !window.location.pathname.includes("/verification")
    ) {
      setRedirectAvailable(true);
      navigate(withLocale(locale, "/verification"));
      return;
    }

    if (
      (user.onboardingCompleted || user.role === "demo") &&
      pathname.includes("/verification")
    ) {
      setRedirectAvailable(true);
      goToProduct();
      return;
    }

    if (
      (user.onboardingCompleted || user.role === "demo") &&
      (pathname === "/" || pathname.match(/^\/[a-z]{2}$/))
    ) {
      setRedirectAvailable(true);
      goToProduct();
      return;
    }

    setRedirectAvailable(false);
  }, [user, navigate, pathname]);

  return {
    redirectAvailable,
  };
};
